/**
 * Beskeden en regel sender til Teams-kanalen, som den staar i `codepass_domain_rules.teams_message`.
 *
 * **Ét tekstfelt, ikke tre kolonner.** Omtaler skrives inde i teksten, saa den der redigerer
 * reglen skriver beskeden ét sted og ser den i den raekkefoelge, modtageren laeser den:
 *
 *   `@{bkha@onecode.dk}`   en omtale. Emailen er noeglen - se `MENTION`
 *   `{{opgave}}`           en plads, reglen selv fylder ud - se `applyContext`
 *
 * Parsningen ligger i `packages/shared`, fordi baade formularen paa /admin/regler og
 * afsendelsen i API'et skal forstaa den ens: en forhaandsvisning der viser noget andet end det
 * kortet bliver til, er vaerre end ingen forhaandsvisning.
 *
 * Ukendte markeringer er ikke fejl, der stopper afsendelsen. En omtale af en email, ingen
 * bruger har, bliver til teksten uden `@{}` og staar i `unknownMentions`, saa formularen kan
 * sige det - men kortet gaar ud. En alarm der ikke bliver sendt, fordi nogen skrev en
 * emailadresse forkert, er den forkerte vej at fejle.
 */

/** En bruger, der kan omtales. Samme form som /admin/brugere kender dem. */
export interface MentionableUser {
  /** Vises i kortet. Falder tilbage paa emailen, naar profilen ikke har et navn. */
  name: string;
  /**
   * Teams' noegle paa en person (UPN). Det er den `mentioned.id` skal vaere, og derfor er den
   * ogsaa noeglen i selve teksten - et navn kunne to personer dele.
   */
  email: string;
}

/** En omtale, klar til baade tekst og `msteams.entities`. */
export interface TeamsMention {
  name: string;
  email: string;
  /** `<at>Navn</at>` - praecis den streng, der skal staa i teksten og i entiteten. */
  tag: string;
}

export interface ParsedTeamsMessage {
  /** Brodteksten, ét afsnit pr. element, med omtalerne skiftet ud med deres `<at>`-tag. */
  lines: string[];
  /** Omtalerne, uden dubletter. Tom naar beskeden ikke naevner nogen. */
  mentions: TeamsMention[];
  /** Emails der blev omtalt, men ikke findes blandt brugerne. Til en advarsel i formularen. */
  unknownMentions: string[];
}

/**
 * Eksempelvaerdier til `{{…}}`, naar der ikke er nogen aegte anledning.
 *
 * Bruges af forhaandsvisningen paa /admin/regler og af testkortet, saa de to viser det samme.
 * Reglerne har endnu ingen evaluering - `DOMAIN_RULE_EVALUATIONS` er tom - saa i dag er det
 * den eneste kontekst, der findes.
 */
export const TEAMS_MESSAGE_EXAMPLE_CONTEXT: Readonly<Record<string, string>> = {
  opgave: 'Fakturamotor',
  kontrakt: 'ONC-1011',
  milepael: 'S01',
};

/**
 * `@{email}`. Kroellparenteserne er der, fordi en email ikke har nogen tydelig slutning:
 * `@bkha@onecode.dk, og så…` kan ikke deles op uden at gaette paa kommaet.
 */
const MENTION = /@\{([^{}\s]+)\}/g;

/** `{{noegle}}`. Se `applyContext`. */
const PLACEHOLDER = /\{\{([a-z][a-z0-9_]*)\}\}/g;

/**
 * Fylder `{{noegle}}` ud med det, reglen ved om den konkrete anledning.
 *
 * Det er dét, der lader beskeden naevne **aarsagen** og ikke bare sige at noget skete: en
 * regel, der finder en opgave over sit estimat, giver fx `{ opgave: 'Fakturamotor' }`, og
 * teksten kan skrive den ind, hvor den hoerer hjemme.
 *
 * Ukendte noegler bliver staaende som de er. De er langt oftere en stavefejl end en tom
 * vaerdi, og en tekst med `{{opgve}}` i siger tydeligere hvad der er galt end et hul i
 * saetningen. Reglerne har endnu ingen evaluering - `DOMAIN_RULE_EVALUATIONS` er tom - saa i
 * dag er konteksten tom overalt undtagen i forhaandsvisningen.
 */
export function applyContext(text: string, context: Readonly<Record<string, string>>): string {
  return text.replace(PLACEHOLDER, (match, key: string) => context[key] ?? match);
}

/** Slaar brugere op paa email, uafhaengigt af store og smaa bogstaver. */
function directory(users: readonly MentionableUser[]): Map<string, MentionableUser> {
  const byEmail = new Map<string, MentionableUser>();
  for (const user of users) {
    const key = user.email.trim().toLowerCase();
    if (key) byEmail.set(key, user);
  }
  return byEmail;
}

/** Deler beskeden op i tekst og omtaler. */
export function parseTeamsMessage(
  message: string | null | undefined,
  users: readonly MentionableUser[] = [],
  context: Readonly<Record<string, string>> = {},
): ParsedTeamsMessage {
  const raw = applyContext((message ?? '').trim(), context);
  if (raw.length === 0) return { lines: [], mentions: [], unknownMentions: [] };

  const byEmail = directory(users);
  const mentions = new Map<string, TeamsMention>();
  const unknown = new Set<string>();

  const withMentions = raw.replace(MENTION, (_match, email: string) => {
    const key = email.trim().toLowerCase();
    const user = byEmail.get(key);

    if (!user) {
      unknown.add(email.trim());
      // Uden markeringen frem for med: modtageren skal laese en adresse, ikke en syntaksfejl.
      return email.trim();
    }

    const tag = `<at>${user.name}</at>`;
    mentions.set(key, { name: user.name, email: user.email, tag });
    return tag;
  });

  /*
   * Afsnit deles paa linjeskift. To linjeskift i traek er stadig ét afsnitsskift - en tom
   * TextBlock ville vaere et hul i kortet, ikke en luftlinje.
   */
  const lines = withMentions
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0);

  return {
    lines,
    mentions: [...mentions.values()],
    unknownMentions: [...unknown],
  };
}

/** Markeringen der omtaler en bruger. Formularen indsaetter den; parseren laeser den. */
export function mentionToken(email: string): string {
  return `@{${email.trim()}}`;
}

/**
 * Beskeden som ren tekst, til alt der ikke er et Teams-kort.
 *
 * Alarmen gemmer sin besked med omtalerne staaende som `@{email}` - se user-alarm-inbox.ts -
 * fordi oversaettelsen til `<at>`-tags kraever brugerlisten og derfor foerst kan ske ved
 * afsendelsen. Portalen har den samme tekst at vise, men hverken `@{}` eller `<at>` betyder
 * noget dér: det foerste ligner en syntaksfejl, det andet en tag, der slap ud af en skabelon.
 *
 * Her bliver de to til det, de betyder - personens navn - saa alarmen kan laeses det samme sted,
 * den bliver handlet paa.
 */
export function plainTeamsMessage(
  message: string | null | undefined,
  users: readonly MentionableUser[] = [],
  context: Readonly<Record<string, string>> = {},
): string {
  return parseTeamsMessage(message, users, context)
    .lines.join(' ')
    .replace(/<\/?at>/g, '');
}
